import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  SportsCricket,
  PersonOutline,
  ExpandMore,
  Search,
  Clear,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  SwapHoriz,
  DeleteSweep,
  History,
  CheckCircleOutline,
  PushPin,
  PushPinOutlined, FactCheck } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAutopilotPage } from '../autopilot/registry';
import { useNavigate } from 'react-router-dom';
import { writeHandoff, HandoffLineup } from '../utils/scorecardHandoff';
import TeamLogo from './TeamLogo';
import { getRoleColor, getRoleLabel } from '../utils/roles';
import {
  TeamInfoResponse,
  TeamPlayerDetail,
  TradeEvaluationResponse,
  LineupEvaluation,
  TradeImpactComparison,
} from '../types/lineup';
import { clearAllCaches } from '../utils/cacheUtils';

interface TeamMapping {
  id: number;
  name: string;
  colors: {
    primary: string;
    secondary: string;
  };
}

interface TwoTeamsTradeProps {
  teamMappings: { [key: string]: TeamMapping };
}

// Default empty team mapping to prevent undefined errors
const DEFAULT_TEAM_MAPPING: TeamMapping = {
  id: 0,
  name: 'Unknown Team',
  colors: { primary: '#000000', secondary: '#000000' }
};

// Player chips in the sticky trade bar. Filled rather than outlined, so the row
// reads as a set of solid tokens instead of a line of empty boxes, and the
// delete icon stays quiet until hovered.
const stickyChipSx = {
  flexShrink: 0,
  height: 26,
  borderRadius: 99,
  fontSize: '0.78125rem',
  fontWeight: 500,
  bgcolor: 'action.selected',
  border: 'none',
  '& .MuiChip-label': { px: 1.25 },
  '& .MuiChip-deleteIcon': {
    fontSize: 16,
    mr: 0.5,
    ml: -0.25,
    color: 'text.disabled',
    '&:hover': { color: 'error.main' },
  },
} as const;

// Custom hook for localStorage persistence
function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error loading ${key} from localStorage:`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error saving ${key} to localStorage:`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
}

// Custom hook to get previous value
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// Progress Tracker Component
interface ProgressTrackerProps {
  loading: boolean;
  startTime: number | null;
  elapsedTime: number | null;
  message: string;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  loading,
  startTime,
  elapsedTime,
  message
}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (loading && startTime) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 100); // Update every 100ms for smooth display
      return () => clearInterval(interval);
    }
  }, [loading, startTime]);

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const runningTime = startTime ? (currentTime - startTime) / 1000 : 0;
  const displayTime = elapsedTime !== null ? elapsedTime : runningTime;

  if (!loading && elapsedTime === null) return null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
        <Typography variant="body2" color="primary" fontWeight="bold">
          {loading ? `${formatTime(runningTime)} elapsed` : `✓ Completed in ${formatTime(displayTime)}`}
        </Typography>
      </Box>
      <Box>
        <LinearProgress 
          variant={loading ? "indeterminate" : "determinate"}
          value={loading ? undefined : 100}
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: loading ? 'primary.main' : 'success.main'
            }
          }}
        />
      </Box>
      {!loading && elapsedTime !== null && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          Trade evaluation completed successfully!
        </Typography>
      )}
    </Box>
  );
};

/** One evaluated trade kept for side-by-side comparison (auto-pilot or manual). */
interface TradeScenario {
  id: string;
  label: string;
  teamA: string;
  teamB: string;
  teamASelections: string[];
  teamBSelections: string[];
  nLineups: number;
  /** Whether traded-in players were forced into their new XI. Optional because
   *  scenarios stored before this setting existed were all run with it on. */
  fixInLineup?: boolean;
  result: TradeEvaluationResponse;
  /** Last time the user opened this scenario — full payloads are kept for the
   *  most recently used ones; older ones are re-evaluated on demand. */
  viewedAt?: number;
}

/** Scenarios predating the setting were evaluated under the backend default. */
const scenarioFixInLineup = (s: TradeScenario) => s.fixInLineup ?? true;

// Keep plenty of scenarios for comparison; browser storage is protected by
// keeping FULL results (lineups, rankings) only for the most recent few —
// older scenarios keep their impact summary, which is what the cards show.
const MAX_SCENARIOS = 20;
/** Scenarios persist to localStorage (~5MB quota); stay well under it. */
const SCENARIO_BYTE_BUDGET = 2_500_000;

// Overall TV per club: the engine's values when present, otherwise the average of the two scenarios.
const overallBat = (r: { overall_bat_tv?: number | null; reg_bat_tv?: number | null; imp_bat_tv?: number | null }): number | null =>
  r.overall_bat_tv != null ? r.overall_bat_tv : r.reg_bat_tv != null && r.imp_bat_tv != null ? (r.reg_bat_tv + r.imp_bat_tv) / 2 : null;
const overallBowl = (r: { overall_bowl_tv?: number | null; sub_bowl_tv?: number | null; ns_bowl_tv?: number | null }): number | null =>
  r.overall_bowl_tv != null ? r.overall_bowl_tv : r.sub_bowl_tv != null && r.ns_bowl_tv != null ? (r.sub_bowl_tv + r.ns_bowl_tv) / 2 : null;

// Impact table formatting: Overall Bat/Bowl come from the backend when it returns them, else shown as a dash.
const fmtTv = (v?: number | null): string => (v != null && Number.isFinite(v) ? v.toFixed(2) : '—');
const diffTv = (after?: number | null, before?: number | null): number | null =>
  after != null && before != null && Number.isFinite(after) && Number.isFinite(before) ? after - before : null;
const fmtDelta = (v?: number | null): string => (v != null && Number.isFinite(v) ? `${v > 0 ? '+' : ''}${v.toFixed(2)}` : '—');
const deltaSx = (v?: number | null) => ({
  color: v == null ? 'text.secondary' : v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.secondary',
  fontWeight: 600,
});

const TwoTeamsTrade: React.FC<TwoTeamsTradeProps> = ({ teamMappings }) => {
  // Persistent state using useLocalStorage
  const [teamA, setTeamA] = useLocalStorage<string>('trade_machine_team_a', '');
  const [teamB, setTeamB] = useLocalStorage<string>('trade_machine_team_b', '');
  const [teamAInfo, setTeamAInfo] = useLocalStorage<TeamInfoResponse | null>('trade_machine_team_a_info', null);
  const [teamBInfo, setTeamBInfo] = useLocalStorage<TeamInfoResponse | null>('trade_machine_team_b_info', null);
  const [teamASelections, setTeamASelections] = useLocalStorage<string[]>('trade_machine_team_a_selections', []);
  const [teamBSelections, setTeamBSelections] = useLocalStorage<string[]>('trade_machine_team_b_selections', []);
  const [tradeResult, setTradeResult] = useLocalStorage<TradeEvaluationResponse | null>('trade_machine_result', null);
  
  // Separate state for updated squads (stored together)
  const [updatedSquads, setUpdatedSquads] = useLocalStorage<{ teamA: TeamPlayerDetail[] | null, teamB: TeamPlayerDetail[] | null }>('trade_machine_updated_squads', { teamA: null, teamB: null });

  // Multi-scenario comparison: every evaluation (manual or auto-pilot) is kept
  // so several what-ifs can be viewed side by side and switched between.
  const [tradeScenarios, setTradeScenarios] = useLocalStorage<TradeScenario[]>('trade_machine_scenarios', []);
  const [activeScenarioId, setActiveScenarioId] = useLocalStorage<string>('trade_machine_active_scenario', '');
  // Scenario currently being re-evaluated after its details were slimmed away.
  const [rehydratingScenarioId, setRehydratingScenarioId] = useState<string | null>(null);
  const teamAUpdatedSquad = updatedSquads.teamA;
  const teamBUpdatedSquad = updatedSquads.teamB;
  
  // Non-persistent state
  const [teamASearch, setTeamASearch] = useState('');
  const [teamBSearch, setTeamBSearch] = useState('');
  const [nLineups, setNLineups] = useState(5);
  // Traded-in players are guaranteed a place in their new XI by default, which
  // measures how good the team is WITH them rather than whether they'd be picked.
  const [fixInLineup, setFixInLineup] = useState(true);
  const [evaluatingTrade, setEvaluatingTrade] = useState(false);
  const [tradeStartTime, setTradeStartTime] = useState<number | null>(null);
  const [tradeElapsedTime, setTradeElapsedTime] = useState<number | null>(null);
  const [currentElapsedTime, setCurrentElapsedTime] = useState<number>(0);
  // Results are segmented: verdict first, detail on demand.
  const [resultsTab, setResultsTab] = useState<'lineups' | 'rankings'>('lineups');
  // Lineup index per team chosen for the Trade Scorecard handoff (default: the best lineup)
  const [scorecardPick, setScorecardPick] = useState<Record<number, number>>({});
  const navigate = useNavigate();
  const [tradeError, setTradeError] = useState('');
  const [loadingTeamA, setLoadingTeamA] = useState(false);
  const [loadingTeamB, setLoadingTeamB] = useState(false);
  const [errorTeamA, setErrorTeamA] = useState<string>('');
  const [errorTeamB, setErrorTeamB] = useState<string>('');

  // Track previous values for change detection
  const prevTeamA = usePrevious(teamA);
  const prevTeamB = usePrevious(teamB);
  
  // Ref for scrolling to trade results
  const tradeResultsRef = useRef<HTMLDivElement>(null);

  // Timer effect for trade evaluation
  useEffect(() => {
    if (evaluatingTrade && tradeStartTime) {
      const interval = setInterval(() => {
        setCurrentElapsedTime((Date.now() - tradeStartTime) / 1000);
      }, 100); // Update every 100ms for smooth display
      return () => clearInterval(interval);
    } else {
      setCurrentElapsedTime(0);
    }
  }, [evaluatingTrade, tradeStartTime]);

  // Format time for display
  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Reset when team actually changes (not on initial mount)
  useEffect(() => {
    if (prevTeamA && prevTeamA !== teamA) {
      setTeamASelections([]);
      setUpdatedSquads(prev => ({ ...prev, teamA: null }));
      setTradeResult(null);
    }
  }, [teamA, prevTeamA, setTeamASelections, setUpdatedSquads, setTradeResult]);

  useEffect(() => {
    if (prevTeamB && prevTeamB !== teamB) {
      setTeamBSelections([]);
      setUpdatedSquads(prev => ({ ...prev, teamB: null }));
      setTradeResult(null);
    }
  }, [teamB, prevTeamB, setTeamBSelections, setUpdatedSquads, setTradeResult]);

  // Fetch team info
  useEffect(() => {
    if (teamA && teamA !== '') {
      setLoadingTeamA(true);
      setErrorTeamA('');
      apiService.getTeamInfoV2({ team: teamA }) // Using V2 API - send team short name
        .then((data) => {
          setTeamAInfo(data);
          setLoadingTeamA(false);
        })
        .catch((error) => {
          setErrorTeamA(error.message || 'Failed to load team info');
          setLoadingTeamA(false);
        });
    } else {
      setTeamAInfo(null);
    }
  }, [teamA]);

  useEffect(() => {
    if (teamB && teamB !== '') {
      setLoadingTeamB(true);
      setErrorTeamB('');
      apiService.getTeamInfoV2({ team: teamB }) // Using V2 API - send team short name
        .then((data) => {
          setTeamBInfo(data);
          setLoadingTeamB(false);
        })
        .catch((error) => {
          setErrorTeamB(error.message || 'Failed to load team info');
          setLoadingTeamB(false);
        });
    } else {
      setTeamBInfo(null);
    }
  }, [teamB]);

  // Handlers
  const handlePlayerClick = (playerName: string, team: 'A' | 'B') => {
    if (team === 'A') {
      setTeamASelections(prev =>
        prev.includes(playerName) ? prev.filter(p => p !== playerName) : [...prev, playerName]
      );
    } else {
      setTeamBSelections(prev =>
        prev.includes(playerName) ? prev.filter(p => p !== playerName) : [...prev, playerName]
      );
    }
  };

  const isPlayerSelected = (playerName: string, team: 'A' | 'B') => {
    return team === 'A' ? teamASelections.includes(playerName) : teamBSelections.includes(playerName);
  };

  const clearSelections = (team: 'A' | 'B') => {
    if (team === 'A') {
      setTeamASelections([]);
    } else {
      setTeamBSelections([]);
    }
  };

  const resetToOriginal = () => {
    // Reset to original squads but keep teams selected and team info
    setTradeResult(null);
    setUpdatedSquads({ teamA: null, teamB: null });
    setTeamASelections([]);
    setTeamBSelections([]);
    // A new trade is being built: no saved scenario is on screen any more,
    // so the history dropdown returns to its placeholder.
    setActiveScenarioId('');
  };

  const handleClearAllCaches = async () => {
    const confirmClear = window.confirm(
      '🗑️ Clear All Caches?\n\n' +
      'This will:\n' +
      '• Clear all browser localStorage (saved selections, lineups, etc.)\n' +
      '• Clear backend rankings cache\n' +
      '• Reload the page\n\n' +
      'Are you sure?'
    );
    
    if (!confirmClear) return;
    
    try {
      setEvaluatingTrade(true);
      const results = await clearAllCaches();
      
      if (results.localStorage && results.backend) {
        alert('✅ All caches cleared successfully!\n\nPage will reload now.');
        window.location.reload();
      } else {
        const message = results.localStorage 
          ? '⚠️ Browser cache cleared but backend cache failed' 
          : '❌ Failed to clear caches';
        alert(message);
        setEvaluatingTrade(false);
      }
    } catch (error) {
      console.error('Error clearing caches:', error);
      alert('❌ Error clearing caches. Check console for details.');
      setEvaluatingTrade(false);
    }
  };


  const filterPlayers = (players: TeamPlayerDetail[], searchQuery: string) => {
    if (!searchQuery.trim()) return players;
    const query = searchQuery.toLowerCase();
    return players.filter(player =>
      player.player_name.toLowerCase().includes(query) ||
      player.role.toLowerCase().includes(query) ||
      player.nationality.toLowerCase().includes(query)
    );
  };

  const getDisplaySquad = (team: 'A' | 'B') => {
    if (team === 'A') {
      return teamAUpdatedSquad || teamAInfo?.players || [];
    }
    return teamBUpdatedSquad || teamBInfo?.players || [];
  };

  const handleEvaluateTrade = async () => {
    const startTime = Date.now();
    const tradeDict: { [key: string]: string } = {};
    teamASelections.forEach(playerName => { tradeDict[playerName] = teamB; });
    teamBSelections.forEach(playerName => { tradeDict[playerName] = teamA; });

    setEvaluatingTrade(true);
    setTradeStartTime(startTime);
    setTradeElapsedTime(null);
    setCurrentElapsedTime(0);
    setTradeError('');

    try {
      const result = await apiService.evaluateTradeV2({
        trade_dict: tradeDict,
        n_lineups: nLineups,
        nationality: 'India',
        fix_in_lineup: fixInLineup ? 1 : 0,
      }, true); // Using V2 API with optimized=true

      setTradeResult(result);

      // Keep this evaluation in the scenario comparison
      upsertScenario({
        label: scenarioLabelFor(teamASelections, teamBSelections, teamA, teamB, fixInLineup),
        teamA,
        teamB,
        teamASelections,
        teamBSelections,
        nLineups,
        fixInLineup,
        result,
      });

      if (teamAInfo && teamBInfo) {
        const teamANew = [...teamAInfo.players.filter(p => !teamASelections.includes(p.player_name))];
        const playersToTeamA = teamBInfo.players.filter(p => teamBSelections.includes(p.player_name));
        teamANew.push(...playersToTeamA);

        const teamBNew = [...teamBInfo.players.filter(p => !teamBSelections.includes(p.player_name))];
        const playersToTeamB = teamAInfo.players.filter(p => teamASelections.includes(p.player_name));
        teamBNew.push(...playersToTeamB);
        
        setUpdatedSquads({ teamA: teamANew, teamB: teamBNew });
      }
      
      // Complete - set elapsed time
      const elapsed = (Date.now() - startTime) / 1000;
      setTradeElapsedTime(elapsed);
      
      // Scroll to trade results after a short delay to allow rendering
      setTimeout(() => {
        tradeResultsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    } catch (error: any) {
      setTradeError(error.message || 'Failed to evaluate trade');
      setTradeElapsedTime(null);
    } finally {
      setEvaluatingTrade(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Auto-pilot registration: while this page is mounted, the assistant's
  // Auto-pilot mode can read its state and drive it. Handlers close over fresh
  // state (registration refreshes every render). NOTE: the agent runs the trade
  // evaluation server-side and hands the result to apply_trade_result — actions
  // are sequenced (teams → players → result) so the team-change reset effects
  // never clobber the applied selections.
  // ---------------------------------------------------------------------------
  useAutopilotPage({
    page: 'trade',
    describeState: () => ({
      team_a: teamA || null,
      team_b: teamB || null,
      team_a_name: (teamA && teamMappings[teamA]?.name) || null,
      team_b_name: (teamB && teamMappings[teamB]?.name) || null,
      available_teams: Object.entries(teamMappings).map(([short, m]) => ({
        short,
        name: m.name,
      })),
      team_a_squad: (teamAInfo?.players || []).map((p) => p.player_name),
      team_b_squad: (teamBInfo?.players || []).map((p) => p.player_name),
      team_a_selections: teamASelections,
      team_b_selections: teamBSelections,
      n_lineups: nLineups,
      fix_in_lineup: fixInLineup ? 1 : 0,
      trade_result: tradeResult
        ? {
            season: tradeResult.season,
            affected_teams: tradeResult.affected_teams,
            impact: tradeResult.trade_impact,
            // Every evaluated lineup per team: XI in batting order + impact
            // sub + TVs, so the agent can show "lineup 3" or "the starting 11".
            lineups: Object.fromEntries(
              Object.entries(
                (tradeResult.team_lineups || {}) as Record<string, any[]>
              ).flatMap(([tid, lineups]) => {
                if (!lineups?.length) return [];
                const short =
                  Object.entries(teamMappings).find(
                    ([, m]) => String(m.id) === String(tid)
                  )?.[0] || String(tid);
                return [[short, lineups.slice(0, 8).map((lu: any, i: number) => ({
                  lineup: lu.lineup_num || i + 1,
                  xi_in_batting_order: (
                    (lu.starting_xi || []).map((p: any) => p?.player_name).filter(Boolean).length
                      ? (lu.starting_xi || []).map((p: any) => p?.player_name).filter(Boolean)
                      : (lu.lineup_players || [])
                  ).slice(0, 12),
                  impact_batter: lu.sub_details?.batter_name ?? null,
                  impact_bowler: lu.sub_details?.bowler_name ?? null,
                  bat_first_tv: lu.batting_first_tv ?? null,
                  bat_second_tv: lu.batting_second_tv ?? null,
                }))]];
              })
            ),
            // Best post-trade lineup's bowling deployments per team — all
            // variants (5/6/7 bowlers, with and without the impact sub) so the
            // agent can answer any "who bowls when?" question.
            best_lineup_deployments: Object.fromEntries(
              Object.entries(
                (tradeResult.team_lineups || {}) as Record<string, any[]>
              ).flatMap(([tid, lineups]) => {
                if (!lineups?.length) return [];
                const best = [...lineups].sort(
                  (a, b) =>
                    (b.batting_first_tv || 0) + (b.batting_second_tv || 0) -
                    ((a.batting_first_tv || 0) + (a.batting_second_tv || 0))
                )[0];
                const compact = (rows: any[]) =>
                  (rows || []).slice(0, 8).map((r: any) => ({
                    bowler: r['Bowler Name'],
                    pp: r['Powerplay Overs'],
                    middle: r['Middle Overs'],
                    death: r['Slog/Death Overs'],
                    total: r['Total Overs'],
                  }));
                const variants: Record<string, any[]> = {};
                for (const nb of ['5', '6', '7']) {
                  const plain = compact(best[`${nb}_bowl_deployment`]);
                  if (plain.length) variants[`${nb}_bowlers`] = plain;
                  const sub = compact(best[`${nb}_bowl_deployment_sub`]);
                  if (sub.length) variants[`${nb}_bowlers_with_impact_sub`] = sub;
                }
                if (!Object.keys(variants).length) return [];
                const short =
                  Object.entries(teamMappings).find(
                    ([, m]) => String(m.id) === String(tid)
                  )?.[0] || String(tid);
                return [[short, variants]];
              })
            ),
          }
        : null,
      evaluating: evaluatingTrade,
    }),
    actions: {
      select_teams: ({ team_a, team_b }: { team_a?: string; team_b?: string }) => {
        if (team_a) setTeamA(team_a);
        if (team_b) setTeamB(team_b);
      },
      select_players: ({
        team_a_players,
        team_b_players,
      }: {
        team_a_players?: string[];
        team_b_players?: string[];
      }) => {
        setTeamASelections(Array.isArray(team_a_players) ? team_a_players : []);
        setTeamBSelections(Array.isArray(team_b_players) ? team_b_players : []);
      },
      set_n_lineups: ({ n }: { n: number }) => {
        setNLineups(Math.max(1, Math.min(20, Number(n) || 5)));
      },
      set_fix_in_lineup: ({ fix_in_lineup }: { fix_in_lineup: number | boolean }) => {
        setFixInLineup(fix_in_lineup === true || Number(fix_in_lineup) === 1);
      },
      clear_selections: () => {
        setTeamASelections([]);
        setTeamBSelections([]);
      },
      // The agent evaluated the trade server-side; display it exactly as if the
      // user had clicked Evaluate. Selections come from args (closures could be
      // one render behind); updated squads are computed when squads are loaded.
      apply_trade_result: (args: {
        result?: TradeEvaluationResponse;
        team_a_players?: string[];
        team_b_players?: string[];
        team_a?: string;
        team_b?: string;
        n_lineups?: number;
        fix_in_lineup?: number;
        scenario_label?: string;
      }) => {
        if (!args.result) return;
        const selA = args.team_a_players || [];
        const selB = args.team_b_players || [];
        const tA = args.team_a || teamA;
        const tB = args.team_b || teamB;
        // The agent may have run the evaluation under a different setting than
        // the one on screen; record what actually produced this result.
        const fixed = args.fix_in_lineup != null ? args.fix_in_lineup === 1 : fixInLineup;
        setTradeResult(args.result);
        setTradeError('');
        // Every agent evaluation joins the scenario comparison
        upsertScenario({
          label: args.scenario_label || scenarioLabelFor(selA, selB, tA, tB, fixed),
          teamA: tA,
          teamB: tB,
          teamASelections: selA,
          teamBSelections: selB,
          nLineups: args.n_lineups || nLineups,
          fixInLineup: fixed,
          result: args.result,
        });
        if (teamAInfo && teamBInfo) {
          const teamANew = [...teamAInfo.players.filter((p) => !selA.includes(p.player_name))];
          teamANew.push(...teamBInfo.players.filter((p) => selB.includes(p.player_name)));
          const teamBNew = [...teamBInfo.players.filter((p) => !selB.includes(p.player_name))];
          teamBNew.push(...teamAInfo.players.filter((p) => selA.includes(p.player_name)));
          setUpdatedSquads({ teamA: teamANew, teamB: teamBNew });
        }
        setTimeout(() => {
          tradeResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      },
    },
  });



  const getTradeImpactIcon = (benefit: string) => {
    switch (benefit) {
      case 'positive': return <TrendingUp color="success" />;
      case 'negative': return <TrendingDown color="error" />;
      default: return <TrendingFlat color="action" />;
    }
  };

  const getTeamLineups = (teamId: number): LineupEvaluation[] | undefined => {
    if (!tradeResult?.team_lineups) return undefined;
    const lineupsByTeam = tradeResult.team_lineups as Record<string | number, LineupEvaluation[]>;
    const lineups = lineupsByTeam[teamId] ?? lineupsByTeam[String(teamId)];
    if (!lineups) return undefined;
    // Best lineup first — display order is by average TV, not generation order.
    return [...lineups].sort(
      (a, b) =>
        (b.batting_first_tv || 0) + (b.batting_second_tv || 0) -
        ((a.batting_first_tv || 0) + (a.batting_second_tv || 0))
    );
  };

  // ---- Scenario comparison helpers ----
  const scenarioHasDetails = (sc: TradeScenario) =>
    Object.keys(sc.result.team_lineups || {}).length > 0;

  /** Scenarios are history: every one keeps its full saved output so viewing
   *  it never re-computes. Only if the whole list outgrows the localStorage
   *  budget are the least-recently-viewed ones trimmed to their impact
   *  summary (rebuildable via the explicit Re-run button). */
  const slimScenarios = (list: TradeScenario[]): TradeScenario[] => {
    const size = (l: TradeScenario[]) => JSON.stringify(l).length;
    if (size(list) <= SCENARIO_BYTE_BUDGET) return list;
    const rank = (sc: TradeScenario) => sc.viewedAt ?? (parseInt(sc.id, 10) || 0);
    const out = [...list];
    const oldestFirst = [...out].sort((a, b) => rank(a) - rank(b));
    for (const victim of oldestFirst) {
      if (size(out) <= SCENARIO_BYTE_BUDGET) break;
      const i = out.findIndex((sc) => sc.id === victim.id);
      if (i >= 0 && scenarioHasDetails(out[i])) {
        out[i] = { ...out[i], result: { ...out[i].result, team_lineups: {}, updated_rankings: [] } };
      }
    }
    return out;
  };

  const upsertScenario = (s: Omit<TradeScenario, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setTradeScenarios((prev) => {
      const rest = prev.filter((p) => p.label !== s.label);
      const list = [...rest, { ...s, id, viewedAt: Date.now() }].slice(-MAX_SCENARIOS);
      return slimScenarios(list);
    });
    setActiveScenarioId(id);
  };

  /** Scenarios are de-duplicated by label, so the forced-XI setting is part of
   *  it — otherwise running the same trade both ways would overwrite the first
   *  result instead of letting the two be compared. */
  const scenarioLabelFor = (
    selA: string[],
    selB: string[],
    tA: string,
    tB: string,
    fixed: boolean = true
  ) =>
    `${selA.length ? selA.join('/') : '—'} ⇄ ${selB.length ? selB.join('/') : 'one-way'} (${tA}–${tB})` +
    (fixed ? '' : ' · merit XI');

  /** Re-apply a stored scenario to the page — always instantly, from its saved
   *  output. Viewing history must never start an evaluation on its own; a
   *  scenario whose heavy payloads were trimmed shows its saved summary with
   *  an explicit Re-run button instead. Teams are set first; selections and
   *  the result follow after the team-change reset effects have run. */
  const activateScenario = (s: TradeScenario) => {
    setActiveScenarioId(s.id);
    setTeamA(s.teamA);
    setTeamB(s.teamB);
    setNLineups(s.nLineups);
    setFixInLineup(scenarioFixInLineup(s));
    setTradeScenarios((prev) =>
      prev.map((sc) => (sc.id === s.id ? { ...sc, viewedAt: Date.now() } : sc)));

    setTimeout(() => {
      setTeamASelections(s.teamASelections);
      setTeamBSelections(s.teamBSelections);
      setTradeResult(s.result);
      setUpdatedSquads({ teamA: null, teamB: null }); // squads may still be loading
      setTimeout(() => {
        tradeResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }, 120);
  };

  /** Rebuild the lineups/rankings a trimmed scenario dropped. Only ever
   *  triggered by the user's Re-run button — never by merely viewing. */
  const rerunScenario = async (s: TradeScenario) => {
    if (rehydratingScenarioId || evaluatingTrade) return; // one evaluation at a time
    try {
      setRehydratingScenarioId(s.id);
      // Same evaluation theater + elapsed timer as a fresh run.
      setEvaluatingTrade(true);
      setTradeStartTime(Date.now());
      setTradeElapsedTime(null);
      setCurrentElapsedTime(0);
      const tradeDict: { [key: string]: string } = {};
      s.teamASelections.forEach((p) => { tradeDict[p] = s.teamB; });
      s.teamBSelections.forEach((p) => { tradeDict[p] = s.teamA; });
      // Re-run under the scenario's own setting, not whatever the toggle
      // happens to be now, or the refreshed numbers wouldn't match its label.
      const result = await apiService.evaluateTradeV2({
        trade_dict: tradeDict,
        n_lineups: s.nLineups,
        nationality: 'India',
        fix_in_lineup: scenarioFixInLineup(s) ? 1 : 0,
      }, true);
      const now = Date.now();
      setTradeScenarios((prev) =>
        slimScenarios(prev.map((sc) =>
          sc.id === s.id ? { ...sc, viewedAt: now, result } : sc))
      );
      setTradeResult(result);
    } catch (e) {
      console.warn('[Scenarios] Re-evaluation failed, keeping saved summary:', e);
    } finally {
      setRehydratingScenarioId(null);
      setEvaluatingTrade(false);
    }
  };

  const deleteScenario = (id: string) => {
    setTradeScenarios((prev) => prev.filter((sc) => sc.id !== id));
    // Deleting the scenario being viewed keeps the on-screen result but
    // clears the selection so the switcher doesn't point at a ghost.
    if (activeScenarioId === id) setActiveScenarioId('');
  };

  const clearScenarios = () => {
    setTradeScenarios([]);
    setActiveScenarioId('');
  };

  /** Per-scenario team impacts, ordered Team A first, then Team B. */
  const scenarioDeltas = (
    s: TradeScenario
  ): Array<{ short: string; delta: number; from?: number; to?: number }> => {
    const idToShort: { [id: number]: string } = {};
    Object.entries(teamMappings || {}).forEach(([short, m]) => {
      idToShort[m.id] = short;
    });
    const rows = Object.entries(s.result.trade_impact || {}).map(([tid, impact]: [string, any]) => ({
      short: idToShort[Number(tid)] || `T${tid}`,
      delta: Number(impact?.delta_team_tv ?? 0),
      from: impact?.original_rank,
      to: impact?.trade_rank,
    }));
    const order = (sh: string) => (sh === s.teamA ? 0 : sh === s.teamB ? 1 : 2);
    return rows.sort((a, b) => order(a.short) - order(b.short));
  };

  /** The best scenario overall: judged by the team common to every scenario
   *  (usually your own side), else by each scenario's strongest gain. */
  const bestScenarioId = (() => {
    if (tradeScenarios.length < 2) return '';
    const deltasBy = tradeScenarios.map((s) => ({ id: s.id, deltas: scenarioDeltas(s) }));
    const shared = deltasBy[0].deltas
      .map((d) => d.short)
      .find((sh) => deltasBy.every((x) => x.deltas.some((d) => d.short === sh)));
    const score = (x: { deltas: Array<{ short: string; delta: number }> }) =>
      shared
        ? x.deltas.find((d) => d.short === shared)?.delta ?? -Infinity
        : Math.max(...x.deltas.map((d) => d.delta));
    return deltasBy.reduce((a, b) => (score(b) > score(a) ? b : a)).id;
  })();

  /** Send this evaluation to the Trade Scorecard with the lineups picked for each club. */
  const openInScorecard = () => {
    if (!tradeResult || !teamAInfo || !teamBInfo) return;
    const toLineup = (l: LineupEvaluation | undefined): HandoffLineup | null => {
      if (!l) return null;
      const sd = l.sub_details; const subId = sd?.batter_id ?? sd?.bowler_id; const subName = sd?.batter_id != null ? sd?.batter_name : sd?.bowler_name;
      return {
        lineup_num: l.lineup_num,
        xi: (l.starting_xi || []).map((p) => ({ player_id: p.player_id, player_name: p.player_name })),
        sub: subId != null ? { player_id: subId, player_name: subName || '' } : null,
        reg_bat_tv: l.reg_bat_tv, imp_bat_tv: l.imp_bat_tv ?? null, ns_bowl_tv: l.ns_bowl_tv, sub_bowl_tv: l.sub_bowl_tv ?? null,
      };
    };
    const team = (short: string, info: typeof teamAInfo, selections: string[]) => {
      const lineups = getTeamLineups(info!.team_id) || [];
      const pick = Math.min(scorecardPick[info!.team_id] ?? 0, Math.max(0, lineups.length - 1));
      const row = (tradeResult.updated_rankings || []).find((r) => Number(r.team_id) === info!.team_id) as any;
      const im = (tradeResult.trade_impact as any)[info!.team_id] || (tradeResult.trade_impact as any)[String(info!.team_id)] || {};
      return {
        short, team_id: info!.team_id, selections,
        lineup: toLineup(lineups[pick]),
        overall_bat_tv: row ? overallBat(row) : null, overall_bowl_tv: row ? overallBowl(row) : null,
        original_bat_tv: im.original_bat_tv ?? null, original_bowl_tv: im.original_bowl_tv ?? null,
        original_team_tv: im.original_team_tv ?? null, trade_team_tv: im.trade_team_tv ?? null,
        original_rank: im.original_rank ?? null, trade_rank: im.trade_rank ?? null,
      };
    };
    writeHandoff({ at: new Date().toISOString(), season: tradeResult.season ?? null, nLineups,
      teams: [team(teamA, teamAInfo, teamASelections), team(teamB, teamBInfo, teamBSelections)] });
    navigate('/trade-scorecard');
  };

  const getTradeTeamsForDisplay = () => {
    const teams: Array<{ teamId: number; teamName: string; teamShort: string }> = [];
    if (teamAInfo) {
      teams.push({ teamId: teamAInfo.team_id, teamName: teamAInfo.team_name, teamShort: teamA });
    }
    if (teamBInfo) {
      teams.push({ teamId: teamBInfo.team_id, teamName: teamBInfo.team_name, teamShort: teamB });
    }
    return teams;
  };

  const getTeamShortColor = (teamShort: string, fallback: string) =>
    (teamMappings && teamMappings[teamShort]?.colors.primary) || fallback;

  const getTradeImpactRows = (): TradeImpactComparison[] => {
    if (!tradeResult) return [];

    const impactByTeam = tradeResult.trade_impact as Record<string | number, TradeImpactComparison>;
    const orderedRows: TradeImpactComparison[] = [];

    getTradeTeamsForDisplay().forEach(({ teamId, teamName, teamShort }) => {
      const impact = impactByTeam[teamId] ?? impactByTeam[String(teamId)];
      if (impact) orderedRows.push({ ...impact, team_name: teamName, team_short: teamShort } as any);
    });

    Object.values(impactByTeam).forEach((impact) => {
      if (!orderedRows.some((row) => row.team_id === impact.team_id)) {
        orderedRows.push(impact);
      }
    });

    return orderedRows;
  };

  const getRankChangeInfo = (teamId: number) => {
    if (!tradeResult || !tradeResult.trade_impact[teamId]) return null;
    
    const impact = tradeResult.trade_impact[teamId];
    const rankChange = impact.original_rank - impact.trade_rank; // Positive means moved up
    
    if (rankChange === 0) return null;
    
    return {
      change: rankChange,
      color: rankChange > 0 ? 'success.main' : 'error.main',
      icon: rankChange > 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />,
      text: rankChange > 0 ? `+${rankChange}` : `${rankChange}`,
    };
  };

  // Lineup Card Component
  interface LineupCardProps {
    lineup: LineupEvaluation;
    teamShort: string;
    best?: boolean;
    picked?: boolean;
    onPick?: () => void;
  }

  const LineupCard: React.FC<LineupCardProps> = ({ lineup, teamShort, best, picked, onPick }) => {
    const [expanded, setExpanded] = useState(false);
    const [metricsExpanded, setMetricsExpanded] = useState(false);
    const teamColor = teamMappings[teamShort]?.colors.primary || 'primary.main';

    return (
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: expanded ? 'primary.main' : 'rgba(30,42,90,0.14)',
          borderRadius: 1.5,
          // MUI rounds only the first/last accordion in a group by default
          '&:first-of-type, &:last-of-type': { borderRadius: 1.5 },
          // Clip the square summary/details backgrounds to the rounded corners
          overflow: 'hidden',
          boxShadow: expanded
            ? '0 6px 20px -8px rgba(231,76,145,0.25)'
            : '0 1px 4px rgba(30,42,90,0.05)',
          transition: 'border-color .15s ease, box-shadow .15s ease',
          '&:before': { display: 'none' },
          '&:hover': { boxShadow: '0 4px 14px -6px rgba(30,42,90,0.18)' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: 'background.paper',
            py: 0.5,
            '&:hover': { bgcolor: 'grey.50' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap', pr: 1 }}>
            <TeamLogo short={teamShort} size={30} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.98rem' }}>
              Lineup #{lineup.lineup_num}
            </Typography>
            {onPick && (
              <Chip
                size="small"
                label={picked ? 'For scorecard' : 'Use in scorecard'}
                color={picked ? 'secondary' : 'default'}
                variant={picked ? 'filled' : 'outlined'}
                onClick={(e) => { e.stopPropagation(); onPick(); }}
                sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700 }}
              />
            )}
            {best && (
              <Chip
                size="small"
                label="Best"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  color: 'success.dark',
                  borderColor: 'success.main',
                  bgcolor: 'transparent',
                }}
              />
            )}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'stretch' }}>
              {[
                {
                  label: 'Avg TV',
                  value: (((lineup.batting_first_tv || 0) + (lineup.batting_second_tv || 0)) / 2).toFixed(2),
                  hot: true,
                },
                { label: 'Bat 1st', value: (lineup.batting_first_tv || 0).toFixed(2), hot: false },
                { label: 'Bat 2nd', value: (lineup.batting_second_tv || 0).toFixed(2), hot: false },
              ].map((stat, i) => (
                <Box
                  key={stat.label}
                  sx={{
                    px: { xs: 1.25, sm: 1.75 },
                    py: 0.5,
                    ml: i > 0 ? { xs: 0.5, sm: 1 } : 0,
                    textAlign: 'center',
                    borderRadius: 1,
                    bgcolor: stat.hot ? 'rgba(231,76,145,0.06)' : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: stat.hot ? 800 : 700,
                      fontSize: stat.hot ? '1rem' : '0.92rem',
                      fontVariantNumeric: 'tabular-nums',
                      color: stat.hot ? 'primary.dark' : 'text.primary',
                      lineHeight: 1.3,
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {/* Starting XI - Now First */}
          {lineup.lineup_players && Array.isArray(lineup.lineup_players) && lineup.lineup_players.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Starting XI
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {lineup.lineup_players.slice(0, 6).map((player, idx) => (
                    <Chip
                      key={idx}
                      label={`${idx + 1}. ${player}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.85rem', justifyContent: 'flex-start', borderColor: 'divider', color: 'text.primary', bgcolor: 'background.paper' }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {lineup.lineup_players.slice(6, 11).map((player, idx) => (
                    <Chip
                      key={idx + 6}
                      label={`${idx + 7}. ${player}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.85rem', justifyContent: 'flex-start', borderColor: 'divider', color: 'text.primary', bgcolor: 'background.paper' }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Impact Substitutes - Now Second */}
          {lineup.sub_details && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'text.primary' }}>
                Impact Substitutes
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {lineup.sub_details?.batter_name && (
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Batter
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {lineup.sub_details?.batter_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pos: {lineup.sub_details?.batter_bat_pos != null ? Math.round(lineup.sub_details.batter_bat_pos) : 'N/A'} | TVAA: {lineup.sub_details?.batter_tvaa?.toFixed(3) || 'N/A'}
                    </Typography>
                  </Box>
                )}
                {lineup.sub_details?.bowler_name && (
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Bowler
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {lineup.sub_details?.bowler_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pos: {lineup.sub_details?.bowler_bat_pos != null ? Math.round(lineup.sub_details.bowler_bat_pos) : 'N/A'} | TVAA: {lineup.sub_details?.bowler_tvaa?.toFixed(3) || 'N/A'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Lineup Value Metrics
              </Typography>
              <Button
                size="small"
                onClick={() => setMetricsExpanded(!metricsExpanded)}
                endIcon={metricsExpanded ? '▲' : '▼'}
                sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
              >
                {metricsExpanded ? 'Less' : 'More'}
              </Button>
            </Box>
            
            {/* Always Visible - Key Metrics */}
            <Stack spacing={0.75}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#f0f7f1',
                  border: '1px solid',
                  borderColor: '#d8ebdb',
                  px: 1.25,
                  py: 0.6,
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Batting 1st TV</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>{(lineup.batting_first_tv || 0).toFixed(2)}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#eef1f8',
                  border: '1px solid',
                  borderColor: '#dbe1f0',
                  px: 1.25,
                  py: 0.6,
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Batting 2nd TV</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>{(lineup.batting_second_tv || 0).toFixed(2)}</Typography>
              </Box>
            </Stack>

            {/* Expandable - Detailed Metrics */}
            {metricsExpanded && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'primary.main' }}>
                  Batting Metrics
                </Typography>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Regular Batting TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{(lineup.reg_bat_tv || 0).toFixed(2)}</Typography>
                  </Box>
                  {lineup.imp_bat_tv != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Impact Batting TV:</Typography>
                      <Typography variant="caption" fontWeight="bold">{(lineup.imp_bat_tv || 0).toFixed(2)}</Typography>
                    </Box>
                  )}
                </Stack>

                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'primary.main' }}>
                  Bowling Metrics
                </Typography>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">5 Bowlers TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{(lineup.five_bowl_tv || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">6 Bowlers TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{(lineup.six_bowl_tv || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">7 Bowlers TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{(lineup.seven_bowl_tv || 0).toFixed(2)}</Typography>
                  </Box>
                  {lineup.six_bowl_tv_sub != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">6 Bowlers TV (Sub):</Typography>
                      <Typography variant="caption" fontWeight="bold">{(lineup.six_bowl_tv_sub || 0).toFixed(2)}</Typography>
                    </Box>
                  )}
                  {lineup.seven_bowl_tv_sub != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">7 Bowlers TV (Sub):</Typography>
                      <Typography variant="caption" fontWeight="bold">{(lineup.seven_bowl_tv_sub || 0).toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">NS Bowling TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{(lineup.ns_bowl_tv || 0).toFixed(2)}</Typography>
                  </Box>
                  {lineup.sub_bowl_tv != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Sub Bowling TV:</Typography>
                      <Typography variant="caption" fontWeight="bold">{(lineup.sub_bowl_tv || 0).toFixed(2)}</Typography>
                    </Box>
                  )}
                </Stack>

                {/* Bowling Deployment Tables */}
                {(lineup['5_bowl_deployment'] || lineup['6_bowl_deployment'] || lineup['7_bowl_deployment']) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1.5, color: 'primary.main' }}>
                      Bowling Deployment (No Impact Sub)
                    </Typography>
                    
                    {/* 5 Bowler Deployment */}
                    {lineup['5_bowl_deployment'] && lineup['5_bowl_deployment'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          5 Bowlers
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['5_bowl_deployment'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* 6 Bowler Deployment */}
                    {lineup['6_bowl_deployment'] && lineup['6_bowl_deployment'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          6 Bowlers
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['6_bowl_deployment'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* 7 Bowler Deployment */}
                    {lineup['7_bowl_deployment'] && lineup['7_bowl_deployment'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          7 Bowlers
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['7_bowl_deployment'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </>
                )}

                {/* Bowling Deployment with Impact Sub */}
                {(lineup['5_bowl_deployment_sub'] || lineup['6_bowl_deployment_sub'] || lineup['7_bowl_deployment_sub']) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1.5, color: 'secondary.main' }}>
                      Bowling Deployment (With Impact Sub)
                    </Typography>
                    
                    {/* 5 Bowler Deployment Sub */}
                    {lineup['5_bowl_deployment_sub'] && lineup['5_bowl_deployment_sub'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          5 Bowlers (Sub)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['5_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* 6 Bowler Deployment Sub */}
                    {lineup['6_bowl_deployment_sub'] && lineup['6_bowl_deployment_sub'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          6 Bowlers (Sub)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['6_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* 7 Bowler Deployment Sub */}
                    {lineup['7_bowl_deployment_sub'] && lineup['7_bowl_deployment_sub'].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                          7 Bowlers (Sub)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lineup['7_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };


  // ---- Scenario tabs strip: the session's what-ifs as top-level chips ----
  const chipSideLabel = (names: string[]) =>
    names.length === 0 ? '—' : names[0] + (names.length > 1 ? ` +${names.length - 1}` : '');
  /** The delta shown on a chip: RR's when RR is in the trade, else the winner's. */
  const chipDelta = (sc: TradeScenario) => {
    const deltas = scenarioDeltas(sc);
    if (!deltas.length) return null;
    return deltas.find((d) => d.short === 'RR') ??
      deltas.reduce((a, b) => (b.delta > a.delta ? b : a));
  };

  const scenarioBar =
    tradeScenarios.length > 0 ? (
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          px: 2.5,
          py: 1.25,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          <History fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            Scenario history
          </Typography>
          <Chip
            label={tradeScenarios.length}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: 'rgba(231,76,145,0.08)',
            }}
          />
        </Box>
        <TextField
          select
          size="small"
          value={tradeScenarios.some((sc) => sc.id === activeScenarioId) ? activeScenarioId : ''}
          onChange={(e) => {
            const sc = tradeScenarios.find((x) => x.id === e.target.value);
            if (sc) activateScenario(sc);
          }}
          sx={{
            flex: 1,
            minWidth: 260,
            maxWidth: 760,
            '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (v) => {
              const sc = tradeScenarios.find((x) => x.id === v);
              if (!sc) {
                return (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    View a saved scenario…
                  </Typography>
                );
              }
              const d = chipDelta(sc);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <TeamLogo short={sc.teamA} size={18} />
                    <Box sx={{ ml: -0.5, display: 'flex' }}>
                      <TeamLogo short={sc.teamB} size={18} />
                    </Box>
                  </Box>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {chipSideLabel(sc.teamASelections)} ⇄ {chipSideLabel(sc.teamBSelections)}
                  </Typography>
                  {d && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                        color: d.delta >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {d.short} {d.delta >= 0 ? '+' : ''}{d.delta.toFixed(2)}
                    </Typography>
                  )}
                  {sc.id === bestScenarioId && (
                    <Typography variant="body2" sx={{ color: 'warning.main', flexShrink: 0 }}>
                      ★
                    </Typography>
                  )}
                </Box>
              );
            },
            MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
          }}
        >
          {[...tradeScenarios].reverse().map((sc) => {
            const d = chipDelta(sc);
            const deltas = scenarioDeltas(sc);
            return (
              <MenuItem key={sc.id} value={sc.id} sx={{ '&:hover .sc-x': { opacity: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <TeamLogo short={sc.teamA} size={20} />
                    <Box sx={{ ml: -0.5, display: 'flex' }}>
                      <TeamLogo short={sc.teamB} size={20} />
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {chipSideLabel(sc.teamASelections)} ⇄ {chipSideLabel(sc.teamBSelections)}
                      {sc.id === bestScenarioId && (
                        <Box component="span" sx={{ color: 'warning.main', ml: 0.5 }}>★</Box>
                      )}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                      {deltas
                        .map((x) => `${x.short} ${x.delta >= 0 ? '+' : ''}${x.delta.toFixed(2)}`)
                        .join(' · ')}
                      {` · ${sc.nLineups} lineups`}
                      {!scenarioHasDetails(sc) ? ' · summary' : ''}
                    </Typography>
                  </Box>
                  {d && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                        color: d.delta >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {d.delta >= 0 ? '+' : ''}{d.delta.toFixed(2)}
                    </Typography>
                  )}
                  <IconButton
                    className="sc-x"
                    size="small"
                    aria-label={`Delete scenario ${sc.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      deleteScenario(sc.id);
                    }}
                    sx={{ p: 0.25, opacity: 0.15, transition: 'opacity .12s ease', flexShrink: 0 }}
                  >
                    <Clear sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </MenuItem>
            );
          })}
        </TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 'auto' }}>
          <Tooltip title="Clear all scenarios">
            <IconButton size="small" color="warning" onClick={clearScenarios}>
              <DeleteSweep fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    ) : null;

  return (
    <Box>
      {/* Team Selection — hidden while results are showing (New Trade brings it back) */}
      {!tradeResult && (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Select Teams
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(teamA || teamB) && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => {
                    setTeamA('');
                    setTeamB('');
                    setTeamAInfo(null);
                    setTeamBInfo(null);
                    setTeamASelections([]);
                    setTeamBSelections([]);
                    setUpdatedSquads({ teamA: null, teamB: null });
                    setTradeResult(null);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Start over
                </Button>
              )}
              <Tooltip title="Clear cached data (browser + backend rankings) and reload">
                <span>
                  <IconButton size="small" color="warning" onClick={handleClearAllCaches} disabled={evaluatingTrade}>
                    <DeleteSweep fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Team A</InputLabel>
                <Select
                  value={teamA}
                  label="Team A"
                  onChange={(e) => setTeamA(e.target.value)}
                  renderValue={(v) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <TeamLogo short={v as string} size={24} />
                      {v as string} - {teamMappings[v as string]?.name || ''}
                    </Box>
                  )}
                >
                  {teamMappings && Object.entries(teamMappings).map(([short, info]) => (
                    <MenuItem key={short} value={short} disabled={short === teamB}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <TeamLogo short={short} size={24} />
                        {short} - {info.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Team B</InputLabel>
                <Select
                  value={teamB}
                  label="Team B"
                  onChange={(e) => setTeamB(e.target.value)}
                  renderValue={(v) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <TeamLogo short={v as string} size={24} />
                      {v as string} - {teamMappings[v as string]?.name || ''}
                    </Box>
                  )}
                >
                  {teamMappings && Object.entries(teamMappings).map(([short, info]) => (
                    <MenuItem key={short} value={short} disabled={short === teamA}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <TeamLogo short={short} size={24} />
                        {short} - {info.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Scenario history: saved outputs, one dropdown pick away — never re-runs */}
      {scenarioBar}

      {/* Players Display - Only show before trade is evaluated */}
      {!tradeResult && (teamA || teamB) && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Team A Players */}
          <Box sx={{ flex: 1 }}>
            {teamA && (
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TeamLogo short={teamA} size={26} />
                        {teamAUpdatedSquad ? `${teamAInfo?.team_name} (Updated)` : (teamAInfo?.team_name || teamMappings[teamA]?.name)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                        {teamAInfo ? `${getDisplaySquad('A').length} players` : 'Loading...'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {teamASelections.length > 0 && (
                        <Chip
                          label={`${teamASelections.length} Selected`}
                          size="small" variant="outlined" sx={{ color: teamMappings[teamA]?.colors.primary || 'primary.main', borderColor: teamMappings[teamA]?.colors.primary || 'primary.main', fontWeight: 600, bgcolor: 'transparent' }}
                        />
                      )}
                      {teamASelections.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => clearSelections('A')}
                          sx={{ color: 'text.secondary', borderColor: 'divider' }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                  {/* Search Box */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search players..."
                    value={teamASearch}
                    onChange={(e) => setTeamASearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: teamASearch && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setTeamASearch('')}>
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {teamASelections.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Sending:
                      </Typography>
                      {teamASelections.map((n) => (
                        <Chip key={n} label={n} size="small" color="primary" variant="outlined"
                          onDelete={() => handlePlayerClick(n, 'A')} />
                      ))}
                    </Box>
                  )}

                  {loadingTeamA && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {errorTeamA && <Alert severity="error">{errorTeamA}</Alert>}
                  {teamAInfo && (
                    <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                      {filterPlayers(getDisplaySquad('A'), teamASearch).map((player, index) => {
                        const isSelected = isPlayerSelected(player.player_name, 'A');
                        return (
                          <ListItem
                            key={index}
                            onClick={() => handlePlayerClick(player.player_name, 'A')}
                            sx={{
                              border: '2px solid',
                              borderColor: isSelected ? (teamMappings[teamA]?.colors.primary || 'primary.main') : 'divider',
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: isSelected ? `${teamMappings[teamA]?.colors.primary || 'primary.main'}10` : 'transparent',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <PersonOutline sx={{ mr: 2, color: teamMappings[teamA]?.colors.primary || 'primary.main' }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {player.player_name}
                                  </Typography>
                                  <Chip
                                    label={getRoleLabel(player.role)}
                                    size="small"
                                    color={getRoleColor(player.role) as any}
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                  <Chip label={player.nationality} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                              }
                              secondary={
                                <React.Fragment>
                                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                    Max TVAA: <strong>{player.max_tvaa != null ? player.max_tvaa.toFixed(3) : 'N/A'}</strong>
                                  </Typography>
                                  {player.bat_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                      Bat: {player.bat_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                  {player.bowl_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span">
                                      Bowl: {player.bowl_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Paper>
            )}
          </Box>

          {/* Team B Players */}
          <Box sx={{ flex: 1 }}>
            {teamB && (
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TeamLogo short={teamB} size={26} />
                        {teamBUpdatedSquad ? `${teamBInfo?.team_name} (Updated)` : (teamBInfo?.team_name || teamMappings[teamB]?.name)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                        {teamBInfo ? `${getDisplaySquad('B').length} players` : 'Loading...'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {teamBSelections.length > 0 && (
                        <Chip
                          label={`${teamBSelections.length} Selected`}
                          size="small" variant="outlined" sx={{ color: teamMappings[teamB]?.colors.primary || 'secondary.main', borderColor: teamMappings[teamB]?.colors.primary || 'secondary.main', fontWeight: 600, bgcolor: 'transparent' }}
                        />
                      )}
                      {teamBSelections.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => clearSelections('B')}
                          sx={{ color: 'text.secondary', borderColor: 'divider' }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                  {/* Search Box */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search players..."
                    value={teamBSearch}
                    onChange={(e) => setTeamBSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: teamBSearch && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setTeamBSearch('')}>
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {teamBSelections.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Sending:
                      </Typography>
                      {teamBSelections.map((n) => (
                        <Chip key={n} label={n} size="small" color="secondary" variant="outlined"
                          onDelete={() => handlePlayerClick(n, 'B')} />
                      ))}
                    </Box>
                  )}

                  {loadingTeamB && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {errorTeamB && <Alert severity="error">{errorTeamB}</Alert>}
                  {teamBInfo && (
                    <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                      {filterPlayers(getDisplaySquad('B'), teamBSearch).map((player, index) => {
                        const isSelected = isPlayerSelected(player.player_name, 'B');
                        return (
                          <ListItem
                            key={index}
                            onClick={() => handlePlayerClick(player.player_name, 'B')}
                            sx={{
                              border: '2px solid',
                              borderColor: isSelected ? (teamMappings[teamB]?.colors.primary || 'secondary.main') : 'divider',
                              borderRadius: 1,
                              mb: 1,
                              bgcolor: isSelected ? `${teamMappings[teamB]?.colors.primary || 'secondary.main'}10` : 'transparent',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <PersonOutline sx={{ mr: 2, color: teamMappings[teamB]?.colors.primary || 'secondary.main' }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {player.player_name}
                                  </Typography>
                                  <Chip
                                    label={getRoleLabel(player.role)}
                                    size="small"
                                    color={getRoleColor(player.role) as any}
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                  <Chip label={player.nationality} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                              }
                              secondary={
                                <React.Fragment>
                                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                    Max TVAA: <strong>{player.max_tvaa != null ? player.max_tvaa.toFixed(3) : 'N/A'}</strong>
                                  </Typography>
                                  {player.bat_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                      Bat: {player.bat_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                  {player.bowl_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span">
                                      Bowl: {player.bowl_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      )}

      {!teamA && !teamB && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SwapHoriz sx={{ fontSize: 72, color: 'grey.400', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Select Teams to Begin
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose teams from the dropdowns above to view their players
          </Typography>
        </Box>
      )}


      {/* Progress Tracker - Hidden for trade machine (timer shown in button instead) */}
      {/* <ProgressTracker
        loading={evaluatingTrade}
        startTime={tradeStartTime}
        elapsedTime={tradeElapsedTime}
        message={`Evaluating trade with ${nLineups} lineups per team...`}
      /> */}

      {/* Evaluation theater: staged progress while the engine runs */}
      {evaluatingTrade && (
        <Paper variant="outlined" sx={{ mt: 4, p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {rehydratingScenarioId ? 'Re-running saved scenario…' : 'Evaluating trade…'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {currentElapsedTime > 0 ? formatTime(currentElapsedTime) : ''} · usually 20–60s
            </Typography>
          </Box>
          <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              { label: 'Rebuilding both squads with the traded players', doneAt: 2 },
              { label: `Generating & evaluating ${nLineups} lineups per team`, doneAt: Infinity },
              { label: 'Re-ranking the league', doneAt: Infinity },
            ].map((st, i) => {
              const done = currentElapsedTime >= st.doneAt;
              const activeStage = !done && (i === 0 || currentElapsedTime >= 2);
              const current = i === 0 ? !done : i === 1 ? currentElapsedTime >= 2 : false;
              return (
                <Box key={st.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {done ? (
                    <CheckCircleOutline color="success" fontSize="small" />
                  ) : current && activeStage ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
                  )}
                  <Typography variant="body2" sx={{ color: done || current ? 'text.primary' : 'text.disabled' }}>
                    {st.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Trade Results */}
      {tradeResult && (
        <Box ref={tradeResultsRef} sx={{ mt: 1 }}>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Trade Evaluation Results
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {teamA === 'RR' || teamB === 'RR' ? (
              <Tooltip title="Create this trade in the Trade Scorecard with the lineups picked on the Lineups tab (the best lineup unless you choose another)">
                <Button variant="outlined" color="secondary" startIcon={<FactCheck />} onClick={openInScorecard} sx={{ textTransform: 'none', px: 2.5 }}>
                  Open in Trade Scorecard
                </Button>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', maxWidth: 260 }}>
                The Trade Scorecard scores RR trades only. Evaluate a trade that involves RR to open it there.
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={<SwapHoriz />}
              onClick={() => {
                // Back to the builder: same teams, clean slate — one click.
                resetToOriginal();
                window.scrollTo({ top: 0 });
              }}
              sx={{ textTransform: 'none', px: 2.5 }}
            >
              New Trade
            </Button>
            </Box>
          </Box>

          {/* Trade Impact: always visible above the tabs, verdict first, exact numbers on demand */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              {(() => {
                const rows = getTradeImpactRows();
                const deltas = rows.map((r) => r.delta_team_tv ?? 0);
                const maxDelta = Math.max(...deltas);
                const net = deltas.reduce((a, b) => a + b, 0);
                const margin = maxDelta - Math.min(...deltas);
                // Honest ribbon: no ribbon on a dead heat; "Marginal edge" under 0.1.
                const ribbon =
                  rows.length < 2 || margin < 0.005
                    ? null
                    : maxDelta >= 0.1
                    ? 'Wins this trade'
                    : maxDelta > 0
                    ? 'Marginal edge'
                    : 'Loses less';
                const gainer = rows.find((r) => (r.delta_team_tv ?? 0) === maxDelta);
                const netLine =
                  net > 0.05
                    ? `Net swing +${net.toFixed(2)} TV across both teams — this trade creates value` +
                      (gainer ? ` · ${gainer.team_name} captures the larger share` : '')
                    : net < -0.05
                    ? `Net swing ${net.toFixed(2)} TV across both teams — this trade destroys value overall` +
                      (gainer && maxDelta > 0 ? ` · only ${gainer.team_name} comes out ahead` : '')
                    : 'Net swing ≈ 0 — value shifts between teams rather than being created';
                // Player movement per team, from the evaluated trade itself —
                // fills the card's right side with the cause, not whitespace.
                const details = (tradeResult.trade_details || {}) as Record<string, string>;
                const shorts = rows.map((r) => String((r as any).team_short || ''));
                const movesFor = (short: string) => {
                  const gets = Object.keys(details).filter((pl) => details[pl] === short);
                  const sends = Object.keys(details).filter(
                    (pl) => details[pl] !== short && shorts.includes(details[pl])
                  );
                  return { gets, sends };
                };
                return (
                  <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    {rows.map((impact) => {
                      const delta = impact.delta_team_tv ?? 0;
                      const winner = ribbon !== null && delta === maxDelta;
                      const up = delta > 0;
                      const flat = delta === 0;
                      const moves = movesFor(String((impact as any).team_short || ''));
                      return (
                        <Paper
                          key={impact.team_id}
                          variant="outlined"
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            height: '100%',
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            // Same border weight on both cards so the pair stays
                            // pixel-identical; only the color marks the winner.
                            borderColor: winner && maxDelta > 0 ? 'success.main' : 'divider',
                            borderWidth: 2,
                          }}
                        >
                          <Box sx={{ minWidth: 220 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap', minHeight: 34 }}>
                              <TeamLogo short={(impact as any).team_short} size={34} />
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {impact.team_name}
                              </Typography>
                              {winner && (
                                <Chip
                                  size="small"
                                  color={maxDelta >= 0.1 ? 'success' : 'default'}
                                  variant={maxDelta >= 0.1 ? 'filled' : 'outlined'}
                                  label={ribbon}
                                  sx={{ fontWeight: 700 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '2.2rem',
                                  fontWeight: 800,
                                  lineHeight: 1,
                                  fontVariantNumeric: 'tabular-nums',
                                  color: up ? 'success.main' : flat ? 'text.secondary' : 'error.main',
                                }}
                              >
                                {up ? '+' : ''}{delta.toFixed(2)}
                              </Typography>
                              {up ? <TrendingUp color="success" /> : flat ? <TrendingFlat color="disabled" /> : <TrendingDown color="error" />}
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em' }}>
                                TEAM TV
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Rank ${impact.original_rank} → ${impact.trade_rank}`}
                                sx={{ fontWeight: 600 }}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`TV ${impact.original_team_tv?.toFixed(2)} → ${impact.trade_team_tv?.toFixed(2)}`}
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                              />
                            </Box>
                          </Box>
                          {(moves.gets.length > 0 || moves.sends.length > 0) && (
                            <Box
                              sx={{
                                textAlign: { xs: 'left', sm: 'right' },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                maxWidth: { sm: '45%' },
                              }}
                            >
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                  GETS
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {moves.gets.length ? moves.gets.join(', ') : '—'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                  SENDS
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {moves.sends.length ? moves.sends.join(', ') : '—'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}
                  >
                    {netLine}
                  </Typography>
                  </>
                );
              })()}

              <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Exact numbers</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>Team</TableCell>
                      <TableCell colSpan={3} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(33, 150, 243, 0.06)', borderRight: '2px solid', borderColor: 'divider' }}>Pre-trade</TableCell>
                      <TableCell colSpan={3} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76, 175, 80, 0.06)', borderRight: '2px solid', borderColor: 'divider' }}>Post-trade</TableCell>
                      <TableCell colSpan={3} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.06)', borderRight: '2px solid', borderColor: 'divider' }}>Delta</TableCell>
                      <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold' }}>Rank Change</TableCell>
                      <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold' }}>Impact</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Overall Bat TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Overall Bowl TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>Overall Team TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Overall Bat TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Overall Bowl TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>Overall Team TV</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Bat</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Bowl</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>Team</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getTradeImpactRows().map((impact) => (
                      <TableRow key={impact.team_id}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <TeamLogo short={(impact as any).team_short} size={28} />
                            {impact.team_name}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{fmtTv(impact.original_bat_tv)}</TableCell>
                        <TableCell align="center">{fmtTv(impact.original_bowl_tv)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>{fmtTv(impact.original_team_tv)}</TableCell>
                        <TableCell align="center">{fmtTv(impact.trade_bat_tv)}</TableCell>
                        <TableCell align="center">{fmtTv(impact.trade_bowl_tv)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>{fmtTv(impact.trade_team_tv)}</TableCell>
                        <TableCell align="center" sx={deltaSx(diffTv(impact.trade_bat_tv, impact.original_bat_tv))}>{fmtDelta(diffTv(impact.trade_bat_tv, impact.original_bat_tv))}</TableCell>
                        <TableCell align="center" sx={deltaSx(diffTv(impact.trade_bowl_tv, impact.original_bowl_tv))}>{fmtDelta(diffTv(impact.trade_bowl_tv, impact.original_bowl_tv))}</TableCell>
                        <TableCell align="center" sx={{ ...deltaSx(impact.delta_team_tv), fontWeight: 'bold', borderRight: '2px solid', borderColor: 'divider' }}>{fmtDelta(impact.delta_team_tv)}</TableCell>
                        <TableCell align="center">
                          {impact.original_rank} → {impact.trade_rank}

                          {impact.rank_change !== 0 && ` (${-impact.rank_change > 0 ? '+' : ''}${-impact.rank_change})`}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {getTradeImpactIcon(impact.trade_benefit)}
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {impact.trade_benefit}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tabs
              value={resultsTab}
              onChange={(_e, v) => setResultsTab(v)}
              sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
            >
              <Tab value="lineups" label="Lineups" />
              <Tab value="rankings" label="League Rankings" />
            </Tabs>
          </Box>

          {/* An older, storage-trimmed scenario: its saved impact summary shows
              instantly; rebuilding lineups/rankings is the user's explicit call. */}
          {Object.keys(tradeResult.team_lineups || {}).length === 0 && (() => {
            const sc = tradeScenarios.find((x) => x.id === activeScenarioId);
            return (
              <Alert
                severity="info"
                sx={{ mb: 3, alignItems: 'center' }}
                action={
                  sc ? (
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      disabled={!!rehydratingScenarioId || evaluatingTrade}
                      onClick={() => rerunScenario(sc)}
                      sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      Re-run evaluation
                    </Button>
                  ) : undefined
                }
              >
                Saved summary — the impact numbers are from this scenario's original run.
                Lineup and ranking details were trimmed to save browser storage; re-run to rebuild them.
              </Alert>
            );
          })()}

          {/* Lineups for Each Team - side by side */}
          {resultsTab === 'lineups' && (
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {getTradeTeamsForDisplay().map(({ teamId, teamName, teamShort }, index) => {
              const lineups = getTeamLineups(teamId);
              const teamColor = getTeamShortColor(teamShort, index === 0 ? 'primary.main' : 'secondary.main');

              return (
                <Box key={teamId} sx={{ flex: 1 }}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <TeamLogo short={teamShort} size={34} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {teamName} - Generated Lineups
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                        {lineups ? `${lineups.length} lineups generated` : 'No lineups generated'}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {lineups ? (
                        lineups.map((lineup, idx) => (
                          <LineupCard key={idx} lineup={lineup} teamShort={teamShort} best={idx === 0}
                            picked={(scorecardPick[teamId] ?? 0) === idx} onPick={() => setScorecardPick((prev) => ({ ...prev, [teamId]: idx }))} />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Lineups for this team were not returned by the evaluation.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
          )}

          {/* Updated League Rankings */}
          {resultsTab === 'rankings' && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Full league standings after the trade ({tradeResult.total_teams_in_league} teams)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Same columns as the league rankings table: Overall Bat TV = (Reg Bat + Imp Bat) / 2, Overall Bowl TV = (Sub Bowl + NS Bowl) / 2, Overall Team TV = Overall Bat TV + 1.49 × Overall Bowl TV.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    {/* Category Header Row */}
                    <TableRow>
                      <TableCell 
                        rowSpan={2} 
                        align="center"
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'grey.100',
                          borderRight: '2px solid',
                          borderColor: 'divider',
                          fontSize: '0.875rem'
                        }}
                      >
                        Rank
                      </TableCell>
                      <TableCell 
                        rowSpan={2} 
                        align="center"
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'grey.100',
                          borderRight: '2px solid',
                          borderColor: 'divider',
                          fontSize: '0.875rem'
                        }}
                      >
                        Team
                      </TableCell>
                      <TableCell 
                        colSpan={2} 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(76, 175, 80, 0.08)', 
                          color: 'success.dark',
                          borderRight: '2px solid',
                          borderColor: 'divider',
                          fontSize: '0.875rem'
                        }}
                      >
                        Batting First Metrics
                      </TableCell>
                      <TableCell 
                        colSpan={2} 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(33, 150, 243, 0.08)', 
                          color: 'info.dark',
                          borderRight: '2px solid',
                          borderColor: 'divider',
                          fontSize: '0.875rem'
                        }}
                      >
                        Batting Second Metrics
                      </TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.08)', color: 'primary.main', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}>
                        <Tooltip title="Average of the two scenarios: Bat TV = (Reg Bat + Imp Bat) / 2, Bowl TV = (Sub Bowl + NS Bowl) / 2"><span>Overall TV</span></Tooltip>
                      </TableCell>
                      <TableCell 
                        rowSpan={2} 
                        align="center"
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(156, 39, 176, 0.08)',
                          color: 'primary.main',
                          fontSize: '0.875rem'
                        }}
                      >
                        Overall Team TV
                      </TableCell>
                    </TableRow>
                    {/* Column Header Row */}
                    <TableRow>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(76, 175, 80, 0.04)',
                          fontSize: '0.8rem'
                        }}
                      >
                        Reg Bat TV
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(76, 175, 80, 0.04)',
                          borderRight: '2px solid', borderColor: 'divider', fontSize: '0.8rem'
                        }}
                      >
                        Sub Bowl TV
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(33, 150, 243, 0.04)',
                          fontSize: '0.8rem'
                        }}
                      >
                        Imp Bat TV
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontWeight: 'bold', 
                          bgcolor: 'rgba(33, 150, 243, 0.04)',
                          borderRight: '2px solid', borderColor: 'divider', fontSize: '0.8rem'
                        }}
                      >
                        NS Bowl TV
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.04)', fontSize: '0.8rem' }}><Tooltip title="Overall Bat TV = (Reg Bat TV + Imp Bat TV) / 2"><span>Bat TV</span></Tooltip></TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.04)', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.8rem' }}><Tooltip title="Overall Bowl TV = (Sub Bowl TV + NS Bowl TV) / 2"><span>Bowl TV</span></Tooltip></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tradeResult.updated_rankings.map((ranking) => {
                      const rankChange = getRankChangeInfo(ranking.team_id);
                      const isAffected = tradeResult.affected_teams.includes(ranking.team_id);
                      
                      return (
                        <TableRow
                          key={ranking.team_id}
                          sx={{
                            bgcolor: isAffected ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                          }}
                        >
                          <TableCell align="center" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography fontWeight="bold">{ranking.rank}</Typography>
                              {rankChange && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: rankChange.color }}>
                                  {rankChange.icon}
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: rankChange.color }}>
                                    {rankChange.text}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>
                            <Typography fontWeight="bold">{ranking.team}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(76, 175, 80, 0.02)' }}>{ranking.reg_bat_tv != null ? ranking.reg_bat_tv.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(76, 175, 80, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{ranking.sub_bowl_tv != null ? ranking.sub_bowl_tv.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(33, 150, 243, 0.02)' }}>{ranking.imp_bat_tv != null ? ranking.imp_bat_tv.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(33, 150, 243, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{ranking.ns_bowl_tv != null ? ranking.ns_bowl_tv.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)' }}>{overallBat(ranking) != null ? overallBat(ranking)!.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{overallBowl(ranking) != null ? overallBowl(ranking)!.toFixed(2) : 'N/A'}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)' }}>
                            <Chip 
                              label={ranking.team_tv != null ? ranking.team_tv.toFixed(2) : 'N/A'} 
                              color="secondary" 
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          )}
        </Box>
      )}

      {/* Updated Squads — collapsed by default; expand only when you want the
          full post-trade rosters */}
      {tradeResult && (
        <Box sx={{ mt: 4 }}>
          <Accordion
            elevation={0}
            disableGutters
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Updated Team Squads
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  full rosters after this trade — expand to view
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>

          {/* Players Display */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Team A Players */}
            <Box sx={{ flex: 1 }}>
              {teamA && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TeamLogo short={teamA} size={26} />
                          {teamAUpdatedSquad ? `${teamAInfo?.team_name} (Updated)` : (teamAInfo?.team_name || teamMappings[teamA]?.name)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                          {teamAInfo ? `${getDisplaySquad('A').length} players` : 'Loading...'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {teamAInfo && (
                      <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                        {getDisplaySquad('A').map((player, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              border: '2px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <PersonOutline sx={{ mr: 2, color: teamMappings[teamA]?.colors.primary || 'primary.main' }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {player.player_name}
                                  </Typography>
                                  <Chip
                                    label={getRoleLabel(player.role)}
                                    size="small"
                                    color={getRoleColor(player.role) as any}
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                  <Chip label={player.nationality} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                              }
                              secondary={
                                <React.Fragment>
                                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                    Max TVAA: <strong>{player.max_tvaa != null ? player.max_tvaa.toFixed(3) : 'N/A'}</strong>
                                  </Typography>
                                  {player.bat_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                      Bat: {player.bat_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                  {player.bowl_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span">
                                      Bowl: {player.bowl_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>

            {/* Team B Players */}
            <Box sx={{ flex: 1 }}>
              {teamB && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TeamLogo short={teamB} size={26} />
                          {teamBUpdatedSquad ? `${teamBInfo?.team_name} (Updated)` : (teamBInfo?.team_name || teamMappings[teamB]?.name)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                          {teamBInfo ? `${getDisplaySquad('B').length} players` : 'Loading...'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {teamBInfo && (
                      <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                        {getDisplaySquad('B').map((player, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              border: '2px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <PersonOutline sx={{ mr: 2, color: teamMappings[teamB]?.colors.primary || 'secondary.main' }} />
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {player.player_name}
                                  </Typography>
                                  <Chip
                                    label={getRoleLabel(player.role)}
                                    size="small"
                                    color={getRoleColor(player.role) as any}
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                  <Chip label={player.nationality} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                              }
                              secondary={
                                <React.Fragment>
                                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                    Max TVAA: <strong>{player.max_tvaa != null ? player.max_tvaa.toFixed(3) : 'N/A'}</strong>
                                  </Typography>
                                  {player.bat_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                                      Bat: {player.bat_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                  {player.bowl_tvaa_per_over !== null && (
                                    <Typography variant="caption" component="span">
                                      Bowl: {player.bowl_tvaa_per_over.toFixed(3)}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Sticky trade ticket: the trade being built, visible from anywhere.
          Sits clear of the Ask SquadLab launcher (bottom-right). */}
      {!tradeResult && teamA && teamB && (teamASelections.length > 0 || teamBSelections.length > 0) && (
        <>
          <Box sx={{ height: 96 }} />
          <Paper
            elevation={0}
            sx={{
              position: 'fixed',
              bottom: 20,
              // Centred and only as wide as its contents, rather than stretched
              // across the viewport. The max width leaves clearance for the Ask
              // SquadLab launcher in the bottom-right corner.
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'max-content',
              maxWidth: { xs: 'calc(100vw - 24px)', md: 'min(920px, calc(100vw - 160px))' },
              zIndex: 1200,
              borderRadius: 99,
              pl: 1.25,
              pr: 1,
              py: 0.875,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(15,23,42,0.08)',
              // A tighter, layered shadow reads as a solid floating object;
              // MUI's elevation={8} blur alone looks washed out on white.
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 1px 2px rgba(0,0,0,.6), 0 12px 32px -8px rgba(0,0,0,.7)'
                  : '0 1px 2px rgba(15,23,42,.08), 0 12px 28px -8px rgba(15,23,42,.22)',
              backdropFilter: 'blur(12px)',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(24,24,27,0.88)'
                  : 'rgba(255,255,255,0.88)',
            }}
          >
            {/* The trade itself. Scrolls sideways rather than wrapping, so the
                bar keeps its height however many players are involved. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                overflowX: 'auto',
                py: 0.25,
                '&::-webkit-scrollbar': { height: 0 },
                scrollbarWidth: 'none',
              }}
            >
              <TeamLogo short={teamA} size={24} />
              {teamASelections.length ? (
                teamASelections.map((n) => (
                  <Chip
                    key={n}
                    label={n}
                    size="small"
                    onDelete={() => handlePlayerClick(n, 'A')}
                    sx={stickyChipSx}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                  no one leaves
                </Typography>
              )}

              <SwapHoriz sx={{ color: 'text.disabled', mx: 0.25, flexShrink: 0 }} fontSize="small" />

              <TeamLogo short={teamB} size={24} />
              {teamBSelections.length ? (
                teamBSelections.map((n) => (
                  <Chip
                    key={n}
                    label={n}
                    size="small"
                    onDelete={() => handlePlayerClick(n, 'B')}
                    sx={stickyChipSx}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                  no one returns
                </Typography>
              )}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            <TextField
              type="number"
              size="small"
              value={nLineups}
              onChange={(e) =>
                setNLineups(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 5)))
              }
              // No floating label: the bar is tight on height, and the adornment
              // says what the number means without adding a row. Free entry,
              // clamped 1–20 to match the engine's cap.
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Lineups
                    </Typography>
                  </InputAdornment>
                ),
                inputProps: { min: 1, max: 20 },
              }}
              sx={{
                flexShrink: 0,
                width: 118,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 99,
                  bgcolor: 'action.hover',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  '& fieldset': { border: 'none' },
                },
                '& input': { py: 0.75, pl: 0.5, textAlign: 'center' },
              }}
            />

            {/* Off is the unusual choice, so the pill is lit when on and quiet
                when off, and the tooltip spells out both readings — the bar has
                no room for helper text. */}
            <Tooltip
              title={
                fixInLineup
                  ? 'Traded-in players are guaranteed a place in their new XI. Click to make them earn it.'
                  : 'Traded-in players must earn a place on impact. Click to guarantee them one.'
              }
            >
              <Chip
                size="small"
                clickable
                role="switch"
                aria-checked={fixInLineup}
                aria-label="Force traded players into the XI"
                icon={
                  fixInLineup
                    ? <PushPin sx={{ fontSize: 15 }} />
                    : <PushPinOutlined sx={{ fontSize: 15 }} />
                }
                label="Force XI"
                onClick={() => setFixInLineup((v) => !v)}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  borderRadius: 99,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  bgcolor: fixInLineup ? 'primary.main' : 'action.hover',
                  color: fixInLineup ? 'primary.contrastText' : 'text.secondary',
                  '& .MuiChip-icon': { color: 'inherit', ml: 1 },
                  '&:hover': {
                    bgcolor: fixInLineup ? 'primary.dark' : 'action.selected',
                  },
                }}
              />
            </Tooltip>

            <Button
              variant="contained"
              disableElevation
              disabled={evaluatingTrade}
              startIcon={
                evaluatingTrade ? <CircularProgress size={15} color="inherit" /> : <SwapHoriz />
              }
              onClick={handleEvaluateTrade}
              sx={{
                flexShrink: 0,
                borderRadius: 99,
                px: 2.5,
                py: 0.875,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                // While evaluating the button is disabled, but MUI's default
                // disabled grey makes the running timer hard to read. Keep the
                // brand colour and dim it instead.
                '&.Mui-disabled': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  opacity: 0.65,
                },
              }}
            >
              {evaluatingTrade
                ? `Evaluating… ${currentElapsedTime > 0 ? formatTime(currentElapsedTime) : ''}`
                : 'Evaluate Trade'}
            </Button>

            <Tooltip title="Clear this trade">
              <IconButton
                size="small"
                onClick={() => {
                  setTeamASelections([]);
                  setTeamBSelections([]);
                }}
                sx={{ flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TwoTeamsTrade;

